/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* istanbul ignore file */
import React from 'react'
import {
  getAmiDataOptions
} from '../../device-details/components/dataSourceComponents/ChartOptions'
import * as HighCharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { Card, CardContent, CircularProgress, Grid, Divider, Typography, Box } from '@mui/material'
import AuthError from '../../../components/ErrorComponents/AuthError'
import LanguageStrings from '../../../i18n/locales'
import { useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import deleteIcon from '../../../assets/deleteIcon.svg'
import { type KpiKey } from '../redux/changeKpiType'
import { getKpiInfo } from './KpiComponents/kpiInfo'
import { parseDateInAOHstandard } from '../../../customHooks/parseDate'
import GenericToggleButton, { type ToggleOption } from '../../../components/GenericToggleButton'
import { type ThirtyDayDataObjWithCategory } from '../services/thirtyDayService'
import { type GeneratedData, MultiSelectDropdown } from './MultiSelectDropdown'
import dayjs from 'dayjs'
import type Highcharts from 'highcharts'
import { type thirtyDayState } from '../redux/thirtyDaySlice'

// Palette for multiple charts
const colorPalette: string[] = ['#A78D34', '#1271B1', '#4881FE']
const RULER_BLUE = '#4FAAFF'

interface DateRulerProps {
  start: dayjs.Dayjs
  end: dayjs.Dayjs
  onSelect: (x: number) => void
  selectedX: number | null
  height?: number
  format?: string
  className?: string
  rulerColor?: string
  pinColor?: string
}

export function DateRuler ({
  start,
  end,
  onSelect,
  selectedX,
  height = 56,
  format = 'DD/MM/YY',
  className,
  rulerColor = '#A0A0A0',
  pinColor = '#4FAAFF'
}: DateRulerProps) {
  const ref = React.useRef<HTMLDivElement | null>(null)

  const baselineY = Math.round(height * 0.40)

  const calcFromClientX = (clientX: number) => {
    const el = ref.current
    if (el === null) return null
    const rect = el.getBoundingClientRect()
    const rel = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const x = start.valueOf() + rel * (end.valueOf() - start.valueOf())
    return { x: Math.round(x), px: Math.round(rel * rect.width) }
  }
  const onClickOrTap = (clientX: number) => {
    const res = calcFromClientX(clientX)
    if (res === null) return
    onSelect(res.x)
  }
  const pinRel = React.useMemo(() => {
    if (selectedX == null) return null
    const startMs = start.valueOf()
    const endMs = end.valueOf()
    const span = Math.max(1, endMs - startMs)
    return Math.max(0, Math.min(1, (selectedX - startMs) / span))
  }, [selectedX, start, end])

  const mid = start.add(end.diff(start) / 2, 'ms')

  return (
    <div
      ref={ref}
      className={className}
      style={{
        width: '100%',
        height,
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-start',
        userSelect: 'none',
        cursor: 'pointer'
      }}
      onMouseMove={(e) => { onClickOrTap(e.clientX) }}
      onTouchStart={(e: React.TouchEvent) => {
        const touch = e.touches?.[0]
        if (touch === null) return
        onClickOrTap(touch.clientX)
      }}
    >
      {/* Baseline ruler */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: baselineY,
          height: 1,
          background: rulerColor
        }}
      />
      {/* Vertical pointer (perpendicular to ruler) & pin dot */}
      {pinRel != null && (
        <>
          <div
            style={{
              position: 'absolute',
              left: `${pinRel * 100}%`,
              transform: 'translateX(-0.5px)',
              top: 0,
              height: baselineY,
              width: 1,
              background: pinColor,
              pointerEvents: 'none'
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: `${pinRel * 100}%`,
              transform: 'translateX(-4px)',
              top: baselineY - 4,
              width: 8,
              height: 8,
              borderRadius: 8,
              background: pinColor,
              boxShadow: '0 0 0 1px #0D1B2A'
            }}
          />
        </>
      )}

      {/* Start / Mid / End labels BELOW the ruler */}
      <div
        style={{
          position: 'absolute',
          top: baselineY + 8,
          left: 0,
          width: '33%',
          color: '#BDBDBD',
          fontSize: 12,
          textAlign: 'center'
        }}
      >
        {start.format(format)}
      </div>

      <div
        style={{
          position: 'absolute',
          top: baselineY + 8,
          left: '33%',
          width: '34%',
          color: '#BDBDBD',
          fontSize: 12,
          textAlign: 'center'
        }}
      >
        {mid.format(format)}
      </div>

      <div
        style={{
          position: 'absolute',
          top: baselineY + 8,
          right: 0,
          width: '33%',
          color: '#BDBDBD',
          fontSize: 12,
          textAlign: 'center'
        }}
      >
        {end.format(format)}
      </div>
    </div>
  )
}

interface PointerHandles {
  dot?: Highcharts.SVGElement
}

export function useSynchronizedChartsWithRuler () {
  const chartsRef = React.useRef<Map<string, Highcharts.Chart>>(new Map())
  const pointersRef = React.useRef<Map<string, PointerHandles>>(new Map())
  const [activeX, setActiveX] = React.useState<number | null>(null)

  const registerChart = React.useCallback((asset: string, chart?: Highcharts.Chart) => {
    if (chart === undefined) return
    chartsRef.current.set(asset, chart)
  }, [])

  const reflowAll = React.useCallback(() => {
    for (const [, chart] of chartsRef.current.entries()) {
      try {
        chart.reflow?.()
      } catch {
        // no-op
      }
    }
  }, [])

  const unregisterChart = React.useCallback((asset: string) => {
    chartsRef.current.delete(asset)
    const p = pointersRef.current.get(asset)
    if (p?.dot !== null && p?.dot !== undefined) p.dot.destroy()
    pointersRef.current.delete(asset)
  }, [])

  const clearPointers = React.useCallback((asset: string) => {
    const p = pointersRef.current.get(asset)
    if (p?.dot !== null && p?.dot !== undefined) p.dot.destroy()
    pointersRef.current.delete(asset)
  }, [])

  // Sync all registered charts to the given timestamp
  const syncAllChartsToX = React.useCallback((x: number | null) => {
    for (const [asset, chart] of chartsRef.current.entries()) {
      try {
        const xa = chart.xAxis?.[0]
        if (xa === undefined) continue

        xa.removePlotLine('hover-x')

        if (x == null) {
          clearPointers(asset)
          chart.tooltip?.hide(0)
          chart.redraw(false)
          continue
        }

        const s = chart.series?.[0]
        const pts: Highcharts.Point[] = s?.points ?? []
        if (pts.length === 0) continue

        let nearest: Highcharts.Point | null = null
        let best = Number.POSITIVE_INFINITY
        for (const p of pts) {
          const dx = Math.abs(p.x - x)
          if (dx < best) {
            best = dx; nearest = p
          }
        }
        if (nearest === null) continue
        xa.addPlotLine({
          id: 'hover-x',
          color: RULER_BLUE,
          width: 1,
          value: nearest.x,
          zIndex: 1
        })

        clearPointers(asset)
        const cx = chart.plotLeft + (nearest.plotX ?? 0)
        const cy = chart.plotTop + (nearest.plotY ?? 0)
        const dot = chart.renderer
          .circle(cx, cy, 4)
          .attr({
            fill: RULER_BLUE,
            stroke: '#0D1B2A',
            'stroke-width': 1.25,
            zIndex: 2
          })
          .add()

        pointersRef.current.set(asset, { dot })

        chart.tooltip?.refresh(nearest)
        chart.redraw(false)
      } catch {
        // no-op
      }
    }
  }, [clearPointers])

  React.useEffect(() => {
    syncAllChartsToX(activeX)
  }, [activeX, syncAllChartsToX])

  React.useEffect(() => {
    return () => {
      for (const [, chart] of chartsRef.current.entries()) {
        try { chart.xAxis?.[0]?.removePlotLine('hover-x') } catch {}
      }
      chartsRef.current.clear()
      pointersRef.current.forEach(p => p.dot?.destroy())
      pointersRef.current.clear()
    }
  }, [])

  return { activeX, setActiveX, registerChart, unregisterChart, RULER_BLUE, reflowAll }
}

interface ChartsGlobalPointerProps {
  hoverX: number | null
  start: dayjs.Dayjs
  end: dayjs.Dayjs
  color: string
  bottomCutoffPx?: number
}

export function ChartsGlobalPointer ({
  hoverX,
  start,
  end,
  color,
  bottomCutoffPx = 0
}: ChartsGlobalPointerProps) {
  if (hoverX == null) return null
  const startMs = start.valueOf()
  const endMs = end.valueOf()
  const span = Math.max(1, endMs - startMs)
  const rel = Math.max(0, Math.min(1, (hoverX - startMs) / span))

  return (
    <div
      style={{
        position: 'absolute',
        left: `${rel * 100}%`,
        transform: 'translateX(-0.5px)',
        top: 0,
        bottom: bottomCutoffPx,
        width: 1,
        background: color,
        zIndex: 1,
        pointerEvents: 'none'
      }}
      aria-hidden
    />
  )
}

interface GetGraphReturn {
  type: 'error' | 'data'
  element: JSX.Element
}
type CategoryKey = string

function getCategoryOptions (data: ThirtyDayDataObjWithCategory[]): ToggleOption[] {
  const categories = data.map(item => item.asset_cat)
  return categories.map(category => ({
    label: category,
    value: category
  }))
}

export const defaultDate = {
  end: dayjs(new Date()),
  start: dayjs(new Date((new Date()).setMonth(new Date().getMonth() - 1)))
}

/**
 * Build daily series for an asset type, ensuring every date in the range
 * [defaultDate.start, defaultDate.end] is present. Missing dates -> y = 0.
 * Output points have both `x` (ms) and `name` ('YYYY-MM-DD').
 */
export function buildChartDataForAssetType (
  rows: GeneratedData[],
  assetType: string,
  range: { start: dayjs.Dayjs, end: dayjs.Dayjs } = defaultDate,
  outFmt = 'YYYY-MM-DD'
): Array<{ x: number, name: string, y: number, label: string }> {
  const byDate = new Map<string, { percentage: number, label: string }>()

  for (const r of rows) {
    if (r.asset_type !== assetType) continue
    if (r.date === undefined) continue
    const d = dayjs(r.date)
    if (!d.isValid()) continue

    const key = d.format(outFmt)
    const val = r.percentage == null ? 0 : r.percentage
    const label = r.name ?? ''

    const prev = byDate.get(key) ?? { percentage: 0, label: '' }
    byDate.set(key, {
      percentage: prev.percentage + val,
      label
    })
  }

  let cursor = range.start.startOf('day')
  const last = range.end.startOf('day')

  const out: Array<{ x: number, name: string, y: number, label: string }> = []
  while (cursor.isBefore(last) || cursor.isSame(last)) {
    const key = cursor.format(outFmt)
    const agg = byDate.get(key) ?? { percentage: 0, label: '' }
    out.push({ x: cursor.valueOf(), name: key, y: agg.percentage, label: agg.label })
    cursor = cursor.add(1, 'day')
  }
  return out
}

function buildCategoryMap (liveData: Array<{ asset_cat: string, dataPoint: any[] }> = []): Record<CategoryKey, GeneratedData[]> {
  const map: Record<CategoryKey, GeneratedData[]> = {}

  for (const cat of liveData) {
    map[cat.asset_cat] = (cat.dataPoint ?? []).map(dp => ({
      name: dp.name,
      asset_type: dp.asset_type,
      percentage: dp.percentage,
      date: dp.date,
      count: dp.count ?? null
    }))
  }

  return map
}

function uniqueOptionsByAssetType (rows: GeneratedData[]): GeneratedData[] {
  const seen = new Set<string>()
  const out: GeneratedData[] = []

  for (const r of rows) {
    if (r.asset_type === '') continue
    if (seen.has(r.asset_type)) continue
    seen.add(r.asset_type)
    out.push(r)
  }
  return out
}
function getLegend (selectedAssetTypes: GeneratedData[], httpStatus: any): any {
  const legendRows: JSX.Element[] = []
  for (let i = 0; i < selectedAssetTypes.length; i++) {
    legendRows.push(
      <div key={`${selectedAssetTypes[i].asset_type}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '85px' }} className='chart-legend-container'>
        <div style={{ background: colorPalette[i % colorPalette.length], width: '4px', height: '20px', borderRadius: '2px' }}/>
        <div title={selectedAssetTypes[i].asset_type} style={{ fontFamily: 'Honeywell Sans Web', fontSize: '16px', fontWeight: 500, whiteSpace: 'normal', wordBreak: 'break-word' }} className='legend-text'>{selectedAssetTypes[i].asset_type}</div>
      </div>
    )
  }
  if (httpStatus !== 401 && httpStatus !== 403) {
    return legendRows
  } else {
    return []
  }
}

function getGraph (thirtyDay: thirtyDayState, activeX: number | null, setActiveX: { (value: React.SetStateAction<number | null>): void, (x: number | null): void }, perAssetCharts: Array<{ asset: string, options: Highcharts.Options }>, registerChart: { (asset: string, chart?: Highcharts.Chart): void, (arg0: string, arg1: HighCharts.Chart | undefined): void }, fetchThirtyData: any, selectedValuesForDropdown: GeneratedData[], cardRef: React.MutableRefObject<HTMLDivElement | null>, chartAreaRef: React.MutableRefObject<HTMLDivElement | null>): GetGraphReturn {
  if (thirtyDay.isLoading) {
    return (
      {
        type: 'error',
        element: (<div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>)
      }
    )
  } else if (thirtyDay.httpStatus === 401) {
    return (
      {
        type: 'error',
        element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>)
      }
    )
  } else if (thirtyDay.httpStatus === 403) {
    return (
      {
        type: 'error',
        element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="accessForbidden"/></div>)
      }
    )
  } else if ((thirtyDay.thirtyDayData === null && thirtyDay.httpStatus === 200 && thirtyDay.loadingError === 'No Data Present')) {
    return (
      {
        type: 'error',
        element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="NoDataPresent"/></div>)
      }
    )
  } else if (
    thirtyDay.thirtyDayData !== undefined &&
    thirtyDay.thirtyDayData !== null
  ) {
    return (
      {
        type: 'data',
        element: (
          <div style={{ height: '100%', width: '100%', minHeight: '0' }} className='chart-content-container'>
            <div
              className='chart-content'
              style={{
                width: '100%',
                minWidth: 0,
                display: 'flex',
                justifyContent: 'space-between',
                minHeight: '100px',
                paddingTop: '10px'
              }}
            >
              {/* legends */}
              <div
                className='chart-legend'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  alignItems: 'flex-start',
                  minWidth: 60
                }}
              >
                {getLegend(selectedValuesForDropdown, thirtyDay.httpStatus)}
              </div>
              {/* graph */}
              <div ref={(node) => { cardRef.current = node; chartAreaRef.current = node }} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%'
                  }}
                >
                  <ChartsGlobalPointer
                    hoverX={activeX}
                    start={defaultDate.start}
                    end={defaultDate.end}
                    color={RULER_BLUE}
                    bottomCutoffPx={56}
                  />
                  <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '4px', width: '100%', zIndex: 2 }}>
                    {perAssetCharts.map(({ asset, options }) => (
                      <Box key={asset} sx={{
                        width: '100%',
                        margin: 0,
                        padding: 0,
                        height: '85px',
                        '& .highcharts-container': {
                          marginTop: '-10px !important'
                        },
                        '& .highcharts-tooltip': {
                          zIndex: '9999 !important'
                        },
                        '& .highcharts-tooltip-container': {
                          zIndex: '9999 !important'
                        }
                      }}>
                        <HighchartsReact
                          highcharts={HighCharts}
                          options={options}
                          callback={(chart: HighCharts.Chart | undefined) => {
                            registerChart(asset, chart)
                          }}
                        />
                      </Box>
                    ))}
                    <div style={{ width: '100%', marginTop: 2 }}>
                      <DateRuler
                        key={activeX == null ? 'ruler-clear' : 'ruler-set'}
                        start={defaultDate.start}
                        end={defaultDate.end}
                        onSelect={setActiveX}
                        selectedX={activeX}
                        height={56}
                        format='DD/MM/YY'
                        pinColor={RULER_BLUE}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    )
  } else {
    return (
      {
        type: 'error',
        element: (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="cannotFetch" retry={fetchThirtyData}/></div>)
      }
    )
  }
}

function useElementWidth<T extends HTMLElement> () {
  const ref = React.useRef<T | null>(null)
  const [width, setWidth] = React.useState(0)

  React.useEffect(() => {
    const el = ref.current
    if (el === null) return

    if (typeof window === 'undefined') return

    const update = () => {
      setWidth(Math.floor(el.getBoundingClientRect().width))
    }

    update()

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(entries => {
        const cr = entries[0]?.contentRect
        if (cr !== null) setWidth(Math.floor(cr.width))
      })
      ro.observe(el)

      return () => { ro.disconnect() }
    }
  }, [])

  return { ref, width }
}
const DataQuality = (props: { fethThirtyDayData: any, tenant: any, fetchThirtydata: any, handleKpiRemove: (kpi: KpiKey) => void }): JSX.Element => {
  const thirtyDay = useSelector((state: RootState) => state.thirtyDay)
  const liveData = thirtyDay.thirtyDayData?.liveData ?? []
  const ThirtyDayStrings = LanguageStrings().AMIDataCollectionStrings.thirtyDay

  // current category and its options
  const [selectedCategory, setSelectedCategory] = React.useState<ToggleOption>({ label: 'Water', value: 'Water' })
  const [categoryOptions, setCategoryOptions] = React.useState<ToggleOption[]>([])

  const cardRef = React.useRef<HTMLDivElement | null>(null)
  const { activeX, setActiveX, registerChart, reflowAll } = useSynchronizedChartsWithRuler()

  React.useEffect(() => {
    const handler = (ev: PointerEvent) => {
      const el = cardRef.current
      if (el === null) return
      const target = ev.target as Node | null
      if (target !== null && el.contains(target)) {
        return
      }
      setActiveX(null)
    }
    document.addEventListener('pointerdown', handler, true)
    return () => {
      document.removeEventListener('pointerdown', handler, true)
    }
  }, [setActiveX])

  const { ref: chartAreaRef, width: chartAreaWidth } = useElementWidth<HTMLDivElement>()
  React.useEffect(() => {
    if (chartAreaWidth <= 0) return

    requestAnimationFrame(() => {
      reflowAll()
    })
  }, [chartAreaWidth, reflowAll])

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const hrRef = React.useRef<HTMLHRElement | null>(null)
  const titleRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    const opts = getCategoryOptions(thirtyDay.thirtyDayData?.liveData ?? [])
    setCategoryOptions(opts)
  }, [thirtyDay.thirtyDayData])

  function getLastUpdated (CalculatedAt: string | null): string {
    const timestamp = CalculatedAt !== null ? new Date(CalculatedAt) : ''
    const dayJsUtcTime = parseDateInAOHstandard(timestamp)

    const lastUpdated = ` ${ThirtyDayStrings.LastUpdated}: ${dayJsUtcTime}`

    return lastUpdated
  }

  const MIN_SELECTION = 1
  const MAX_SELECTION = 20

  // selected asset types by each category
  const [selectedAssetTypeKeysByCategory, setSelectedAssetTypeKeysByCategory] = React.useState<Record<string, string[]>>({})

  // category wise data
  const categoryMap = React.useMemo(() => buildCategoryMap(liveData), [liveData])
  // current category data
  const currentRows = categoryMap[selectedCategory.value] ?? []
  // current category asset types
  const assetTypeOptions = React.useMemo(
    () => uniqueOptionsByAssetType(currentRows),
    [currentRows]
  )

  const optionByType = React.useMemo(() => {
    const m = new Map<string, GeneratedData>()
    for (const opt of assetTypeOptions) m.set(opt.asset_type, opt)
    return m
  }, [assetTypeOptions])
  // current category selected asset types
  const selectedKeys = React.useMemo(() => {
    const stored = selectedAssetTypeKeysByCategory[selectedCategory.value] ?? []
    const allowed = new Set(assetTypeOptions.map(o => o.asset_type))

    // reconcile (remove types no longer present)
    const reconciled = Array.from(new Set(stored)).filter(k => allowed.has(k))

    if (reconciled.length > 0) return reconciled

    // default: first 2 (or fewer if not available)
    return assetTypeOptions.slice(0, MAX_SELECTION).map(o => o.asset_type)
  }, [selectedAssetTypeKeysByCategory, selectedCategory, assetTypeOptions])

  React.useEffect(() => {
    if (assetTypeOptions.length === 0) return

    const prev = selectedAssetTypeKeysByCategory[selectedCategory.value] ?? []
    if (prev.join('|') === selectedKeys.join('|')) return

    setSelectedAssetTypeKeysByCategory(p => ({
      ...p,
      [selectedCategory.value]: selectedKeys
    }))
  }, [selectedCategory, selectedKeys, assetTypeOptions.length, selectedAssetTypeKeysByCategory])

  const selectedValuesForDropdown = React.useMemo(() => {
    return selectedKeys
      .map(k => optionByType.get(k))
      .filter(Boolean) as GeneratedData[]
  }, [selectedKeys, optionByType])

  const handleAssetTypeMultiSelectChange = (values: GeneratedData[]) => {
    const keys = Array.from(new Set(values.map(v => v.asset_type))).slice(0, MAX_SELECTION)

    if (keys.length < MIN_SELECTION) return

    setSelectedAssetTypeKeysByCategory(p => ({
      ...p,
      [selectedCategory.value]: keys
    }))
  }

  const perAssetCharts = React.useMemo(() => {
    const uniqueSelected = Array.from(new Set(selectedKeys))

    return uniqueSelected.map((assetType, idx) => {
      const data = buildChartDataForAssetType(
        currentRows,
        assetType,
        defaultDate,
        'YYYY-MM-DD'
      )
      const color = colorPalette[idx % colorPalette.length]

      const options = getAmiDataOptions(
        color,
        data,
        containerRef,
        hrRef,
        titleRef,
        assetType
      )

      return { asset: assetType, options }
    })
  }, [selectedKeys, currentRows, chartAreaWidth])

  return (
    <div style={{ height: window.innerWidth >= 1896 ? 457 : 382, fontFamily: 'Honeywell Sans Web' }}>
      <Card sx={{ height: '100%', borderRadius: '10px', backgroundColor: '#313030', color: '#f0f0f0' }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontSize: window.innerWidth > 1300 ? '16px' : '13px', padding: '15px', paddingBottom: 0, letterSpacing: '1px', textTransform: 'uppercase' }}>30 DAYS TREND {getKpiInfo('30 Days Trend')}</div>
            <img onClick={() => { props.handleKpiRemove('30 Days Trend') }} src={deleteIcon} style={{ marginRight: 15, cursor: 'pointer' }} alt='delete'/>
          </div>
          <div style={{ padding: '0px 1px 15px 15px', fontSize: 14, fontWeight: 300 }}>
            <span>
              {thirtyDay.calculatedAt !== null && thirtyDay.calculatedAt !== undefined && getLastUpdated(thirtyDay.calculatedAt)}
            </span>
          </div>
        </div>
        <CardContent sx={{ height: '100%' }}>
          {liveData !== undefined && liveData.length > 0
            ? <Grid sx={{ marginTop: '-20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2px' }}>
                  <GenericToggleButton options={categoryOptions} selected={selectedCategory} onChange={(option) => { setSelectedCategory(option) }} />
                  <div style={{ display: 'flex', gap: '5px', fontFamily: 'Honeywell Sans Web', alignItems: 'center' }}>
                    <Typography className='multi-dropdown-label' sx={{ fontSize: '16px', fontWeight: 500 }}>{ThirtyDayStrings.deviceType}</Typography>
                    <MultiSelectDropdown
                      options={assetTypeOptions}
                      selectedValues={selectedValuesForDropdown}
                      onChange={handleAssetTypeMultiSelectChange}
                      placeholder="Select an asset type"
                      minSelection={MIN_SELECTION}
                      maxSelection={MAX_SELECTION}
                    />
                  </div>
                </div>
                <Divider
                  orientation="horizontal"
                  sx={{
                    marginTop: '5px',
                    backgroundColor: '#272727',
                    height: '1px',
                    width: 'auto'
                  }}
                />
              </Grid>
            : null
          }
          <Grid sx={{ width: '100%', height: '75%' }}>
            {getGraph(thirtyDay, activeX, setActiveX, perAssetCharts, registerChart, props.fetchThirtydata, selectedValuesForDropdown, cardRef, chartAreaRef).element}
          </Grid>
        </CardContent>
      </Card>
    </div>
  )
}

export default DataQuality
